import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { DragDropContext } from 'react-beautiful-dnd';
import InventoryColumn from './InventoryColumn';
import SchoolsColumn from './SchoolsColumn';
import OutOfCommissionSection from './OutOfCommissionSection';
import csrfToken from './csrf';
import style from './IdCardPrinterBoard.module.sass';

const byName = (a, b) => (a.school_name || '').localeCompare(b.school_name || '');

const mostRecentDeliveryDate = (school) => school.printers
  .map((printer) => printer.delivered_at)
  .filter(Boolean)
  .sort()
  .pop() || '';

const byRecentDelivery = (a, b) => mostRecentDeliveryDate(b).localeCompare(mostRecentDeliveryDate(a));

const isFullyDelivered = (school) => school.printers.length > 0 && school.printers.every((p) => p.delivered);

const upsertSchool = (schools, updatedSchool) => {
  const index = schools.findIndex((s) => s.school_id === updatedSchool.school_id);
  if (index === -1) return [...schools, updatedSchool];
  const next = [...schools];
  next[index] = updatedSchool;
  return next;
};

const IdCardPrinterBoard = ({
  inventoryPrinters: initialPrinters,
  schools: initialSchools,
  retiredPrinters: initialRetiredPrinters,
  createPrinterUrl,
  createRosterUrl,
  schoolSearchUrl,
  markSchoolDeliveredUrl,
}) => {
  const [inventoryPrinters, setInventoryPrinters] = useState(initialPrinters);
  const [schools, setSchools] = useState(initialSchools);
  const [retiredPrinters, setRetiredPrinters] = useState(initialRetiredPrinters);
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState([]);
  const [hoveredSchoolId, setHoveredSchoolId] = useState(null);
  // Tracks whether the drag in progress is a printer (not a school card), so the
  // mousemove listener below knows whether to bother hit-testing school cards.
  const draggingPrinterRef = useRef(false);

  // react-beautiful-dnd can't reliably detect a printer dropped onto a school
  // card when that card is also a Draggable for the Schools<->Delivered move
  // (see SchoolCard.jsx) -- its own Droppable collision detection never
  // registers the nested drop target. So for a printer drag specifically, we
  // track the cursor ourselves and hit-test school cards by DOM position
  // instead of relying on rbd's destination for it.
  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!draggingPrinterRef.current) return;
      const elementUnderCursor = document.elementFromPoint(event.clientX, event.clientY);
      const schoolCardEl = elementUnderCursor && elementUnderCursor.closest('[data-school-id]');
      setHoveredSchoolId(schoolCardEl ? Number(schoolCardEl.dataset.schoolId) : null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const withBusy = async (busyId, action) => {
    if (busyIds.includes(busyId)) return;
    setBusyIds((prev) => [...prev, busyId]);
    setError(null);
    try {
      await action();
    } catch (requestError) {
      // require_admin redirects instead of returning 401/403 on a lapsed session,
      // and XHR follows redirects transparently, so that case can arrive as a 200
      // carrying unrelated HTML. axios only rejects on a non-2xx status, so a
      // "success" response still needs its own success:true check (each action
      // below does that before touching state); this catch covers actual errors.
      setError('That did not save. Reload the page and try again.');
    } finally {
      setBusyIds((prev) => prev.filter((id) => id !== busyId));
    }
  };

  const addPrinter = (serialNumber, printerLetter) => withBusy('add-printer', async () => {
    const response = await axios.post(
      createPrinterUrl,
      { id_card_printer: { serial_number: serialNumber, printer_letter: printerLetter } },
      { headers: { 'X-CSRF-Token': csrfToken() } },
    );
    if (!response.data || response.data.success !== true) throw new Error('create printer failed');
    setInventoryPrinters((prev) => [...prev, response.data.printer]);
  });

  const addSchool = (schoolId) => withBusy('add-school', async () => {
    const response = await axios.post(
      createRosterUrl,
      { school_id: schoolId },
      { headers: { 'X-CSRF-Token': csrfToken() } },
    );
    if (!response.data || response.data.success !== true) throw new Error('create roster failed');
    setSchools((prev) => upsertSchool(prev, response.data.school));
  });

  const removeRosterEntry = (school) => withBusy(`roster-${school.roster_id}`, async () => {
    const response = await axios.delete(school.remove_roster_url, { headers: { 'X-CSRF-Token': csrfToken() } });
    if (!response.data || response.data.success !== true) throw new Error('remove roster entry failed');
    setSchools((prev) => prev.filter((s) => s.school_id !== school.school_id));
  });

  const assignPrinterToSchool = (printer, schoolId) => withBusy(`printer-${printer.id}`, async () => {
    const response = await axios.patch(
      printer.assign_school_url,
      { school_id: schoolId },
      { headers: { 'X-CSRF-Token': csrfToken() } },
    );
    if (!response.data || response.data.success !== true) throw new Error('assign school failed');

    setInventoryPrinters((prev) => prev.filter((p) => p.id !== printer.id));
    setSchools((prev) => {
      const target = prev.find((s) => s.school_id === schoolId);
      if (!target) return prev;
      const updated = { ...target, printers: [...target.printers, response.data.printer] };
      return upsertSchool(prev, updated);
    });
  });

  // "Unpair" on a printer chip inside a school card: sends the printer back
  // to Inventory without deleting it. It could be paired with any school
  // currently rendered, so drop it from every school's printers list rather
  // than looking up which one first.
  const unassignPrinter = (printer) => withBusy(`printer-${printer.id}`, async () => {
    const response = await axios.patch(
      printer.unassign_school_url,
      {},
      { headers: { 'X-CSRF-Token': csrfToken() } },
    );
    if (!response.data || response.data.success !== true) throw new Error('unassign school failed');

    setSchools((prev) => prev.map((s) => ({
      ...s,
      printers: s.printers.filter((p) => p.id !== printer.id),
    })));
    setInventoryPrinters((prev) => [...prev, response.data.printer]);
  });

  // "Out of Commission" on a printer chip, from Inventory or a school card:
  // pulls it out of rotation entirely. It could currently be sitting in
  // Inventory or paired with any school, so clear it from both rather than
  // looking up where it came from first.
  const retirePrinter = (printer) => withBusy(`printer-${printer.id}`, async () => {
    const response = await axios.patch(printer.retire_url, {}, { headers: { 'X-CSRF-Token': csrfToken() } });
    if (!response.data || response.data.success !== true) throw new Error('retire failed');

    setInventoryPrinters((prev) => prev.filter((p) => p.id !== printer.id));
    setSchools((prev) => prev.map((s) => ({
      ...s,
      printers: s.printers.filter((p) => p.id !== printer.id),
    })));
    setRetiredPrinters((prev) => [...prev, response.data.printer]);
  });

  // Brings a printer back from the For Parts list into Inventory.
  const restorePrinter = (printer) => withBusy(`printer-${printer.id}`, async () => {
    const response = await axios.patch(printer.restore_url, {}, { headers: { 'X-CSRF-Token': csrfToken() } });
    if (!response.data || response.data.success !== true) throw new Error('restore failed');

    setRetiredPrinters((prev) => prev.filter((p) => p.id !== printer.id));
    setInventoryPrinters((prev) => [...prev, response.data.printer]);
  });

  const markSchoolDelivered = (school, toDelivered) => withBusy(`school-${school.school_id}`, async () => {
    const response = await axios.patch(
      markSchoolDeliveredUrl,
      { school_id: school.school_id, delivered: toDelivered },
      { headers: { 'X-CSRF-Token': csrfToken() } },
    );
    if (!response.data || response.data.success !== true) throw new Error('mark delivered failed');
    setSchools((prev) => upsertSchool(prev, { ...school, printers: response.data.printers }));
  });

  const pendingSchools = schools.filter((s) => !isFullyDelivered(s)).sort(byName);
  const deliveredSchools = schools.filter(isFullyDelivered).sort(byRecentDelivery);

  const handleDragStart = (start) => {
    draggingPrinterRef.current = start.source.droppableId === 'inventory';
  };

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    const wasDraggingPrinter = draggingPrinterRef.current;
    draggingPrinterRef.current = false;

    if (wasDraggingPrinter) {
      const printerId = Number(draggableId.replace('printer-', ''));
      const printer = inventoryPrinters.find((p) => p.id === printerId);
      if (printer && hoveredSchoolId) assignPrinterToSchool(printer, hoveredSchoolId);
      setHoveredSchoolId(null);
      return;
    }

    // A school card dragged between the Schools and Delivered columns (or use
    // the Move button either way) -- a plain same-type Draggable/Droppable
    // pair, so rbd's own destination detection works fine here.
    if (!destination || source.droppableId === destination.droppableId) return;
    const schoolId = Number(draggableId.replace('school-', ''));
    const school = schools.find((s) => s.school_id === schoolId);
    if (school) markSchoolDelivered(school, destination.droppableId === 'schools-delivered');
  };

  return (
    <div className={style.board}>
      {error && <div className="alert alert-danger">{error}</div>}
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className={style.columns}>
          <InventoryColumn
            printers={inventoryPrinters}
            busyIds={busyIds}
            onAddPrinter={addPrinter}
            onRetire={retirePrinter}
          />
          <SchoolsColumn
            droppableId="schools-pending"
            title="Schools"
            schools={pendingSchools}
            busyIds={busyIds}
            moveLabel="Mark Delivered →"
            onMove={(school) => markSchoolDelivered(school, true)}
            onRemoveRoster={removeRosterEntry}
            onUnpairPrinter={unassignPrinter}
            onRetirePrinter={retirePrinter}
            hoveredSchoolId={hoveredSchoolId}
            addSchoolForm={{ onAddSchool: addSchool, schoolSearchUrl }}
          />
          <SchoolsColumn
            droppableId="schools-delivered"
            title="Delivered"
            schools={deliveredSchools}
            busyIds={busyIds}
            moveLabel="← Move Back"
            onMove={(school) => markSchoolDelivered(school, false)}
            onUnpairPrinter={unassignPrinter}
            onRetirePrinter={retirePrinter}
            hoveredSchoolId={hoveredSchoolId}
            showDeliveredDate
          />
        </div>
      </DragDropContext>

      <OutOfCommissionSection printers={retiredPrinters} busyIds={busyIds} onRestore={restorePrinter} />
    </div>
  );
};

const printerShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  serial_number: PropTypes.string,
  printer_letter: PropTypes.string,
  model: PropTypes.string,
  notes: PropTypes.string,
  troubleshooting_notes: PropTypes.string,
  delivered: PropTypes.bool.isRequired,
  delivered_at: PropTypes.string,
  edit_url: PropTypes.string.isRequired,
  remove_url: PropTypes.string.isRequired,
  assign_school_url: PropTypes.string.isRequired,
  unassign_school_url: PropTypes.string.isRequired,
  retire_url: PropTypes.string.isRequired,
});

const retiredPrinterShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  serial_number: PropTypes.string,
  model: PropTypes.string,
  notes: PropTypes.string,
  troubleshooting_notes: PropTypes.string,
  retired_at: PropTypes.string,
  edit_url: PropTypes.string.isRequired,
  remove_url: PropTypes.string.isRequired,
  restore_url: PropTypes.string.isRequired,
});

IdCardPrinterBoard.propTypes = {
  inventoryPrinters: PropTypes.arrayOf(printerShape).isRequired,
  schools: PropTypes.arrayOf(PropTypes.shape({
    school_id: PropTypes.number.isRequired,
    school_name: PropTypes.string,
    scode: PropTypes.string,
    roster_id: PropTypes.number,
    remove_roster_url: PropTypes.string,
    printers: PropTypes.arrayOf(printerShape).isRequired,
  })).isRequired,
  retiredPrinters: PropTypes.arrayOf(retiredPrinterShape).isRequired,
  createPrinterUrl: PropTypes.string.isRequired,
  createRosterUrl: PropTypes.string.isRequired,
  schoolSearchUrl: PropTypes.string.isRequired,
  markSchoolDeliveredUrl: PropTypes.string.isRequired,
};

export default IdCardPrinterBoard;
